import Image from 'next/future/image';
import { Dispatch, SetStateAction, useState } from 'react';
import pMap from 'p-map';
import cn from 'classnames';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import { RC_DURATION } from '@lambda/reebelo';
import Loading from '@/components/Loading/Loading';
import LineItem from './LineItem';
import shieldIcon from '@/public/icons/buyback/sell-device/shield.svg';
import useCart, { AddToCartParams } from '@/lib/use-cart';
import { logger } from '@/lib/logger';
import WarrantyDisclaimer from '@/components/collections/[handle]/WarrantyDisclaimer';
import {
  generateWarrantyUpsellContent,
  generateWarrantyUpsellCta,
  generateWarrantyUpsellTitle,
} from '@/lib/reebelocare/helpers';
import settings from '@/settings';
import {
  multipleWarrantyUpsellTitles,
  warrantyBenefitsList,
} from '@/lib/reebelocare/constants';
import checkIcon from '@/public/icons/checkout/check.svg';
import reebeloCareIcon from '@/public/icons/reebelocare/reebelocare.svg';
import MultiLineItem from './MultiLineItem';
import { warrantyUpsellT } from '@/lib/reebelocare/types';
import { ReebeloCareTrackRequest } from '@/lib/analytics/types';
import { trackReebeloCareAdded } from '@/lib/analytics/trackProduct';

const CustomHeader = ({
  warrantyBenefits,
}: {
  warrantyBenefits: { label: string; showInMobile: boolean }[];
}) => {
  const warrantyTitle =
    multipleWarrantyUpsellTitles?.[settings.store] ??
    multipleWarrantyUpsellTitles.default;

  return (
    <div className="flex flex-col items-center gap-4 bg-[#ECFCF5] p-4 sm:flex-row md:p-8">
      <div className="h-[100px] w-[100px] md:h-[125px] md:w-[125px]">
        <Image
          src={reebeloCareIcon}
          alt="ReebeloCare"
          height={300}
          width={300}
          className="h-full w-full object-contain"
        />
      </div>
      <div className="flex-1">
        <h3 className="hidden font-bold md:block">{warrantyTitle}</h3>
        <ul className="space-y-1 md:mt-4">
          {warrantyBenefits.map((benefit) => (
            <li
              key={benefit.label}
              className={cn('flex items-center gap-2', {
                'hidden md:flex': !benefit.showInMobile,
              })}
            >
              <div className="h-4 w-4 shrink-0">
                <Image
                  src={checkIcon}
                  height={50}
                  width={50}
                  alt="Check"
                  className="h-full w-full object-cover"
                />
              </div>
              {benefit.label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const DefaultHeader = () => (
  <>
    <div className="mx-auto flex h-[115px] w-[115px] flex-col items-center justify-center rounded-xl bg-[#ECFCF5] p-4">
      <div className="h-full w-full">
        <Image
          src={shieldIcon}
          alt="ReebeloCare"
          height={300}
          width={300}
          className="h-full w-full object-contain"
        />
      </div>
      <span className="text-sm font-bold">ReebeloCare</span>
    </div>
    <div className="mt-5 text-center">
      <h3 className="text-base font-bold">{generateWarrantyUpsellTitle()}</h3>
      <p className="mt-2 text-sm leading-tight text-gray-700/70">
        {generateWarrantyUpsellContent()}
      </p>
    </div>
  </>
);

const MultipleDevices = ({
  warrantyUpsellList,
  isCheckingOut,
  onContinue,
  setIsCheckingOut,
}: {
  warrantyUpsellList: warrantyUpsellT[];
  isCheckingOut: boolean;
  onContinue: () => void;
  setIsCheckingOut: Dispatch<SetStateAction<boolean>>;
}) => {
  const [rcToBeAdded, setRcToBeAdded] = useState<
    (AddToCartParams & { lineItemId: string })[]
  >([]);
  const cart = useCart();
  const cartId = cart?.cart?.id;

  const isAdditionalMonthsEnabled = warrantyUpsellList.some(
    (l) => Object.keys(l.productWarranty).length > 1,
  );

  const warrantyBenefits =
    warrantyBenefitsList?.[settings.store] ?? warrantyBenefitsList.default;

  const onRcContinue = () => {
    setIsCheckingOut(true);
    pMap(
      rcToBeAdded,
      async (rc) => {
        const result = await cart.add(
          {
            merchandiseId: rc.merchandiseId,
            attributes: rc.attributes,
            quantity: rc.quantity,
          },
          cartId,
        );

        const rcForValue = rc?.attributes?.find(
          (att) => att.key === 'For',
        )?.value;

        if (rcForValue) {
          const rcFor = cart.cart?.lines.edges.find(
            (line) =>
              line.node.merchandise.sku ===
              rcForValue.split('[')[1].split(']')[0],
          )?.node;

          if (rcFor) {
            const { psku } = extractTags(rcFor.merchandise.product.tags);
            const duration = (rc?.attributes?.find(
              (att) => att.key === 'Duration',
            )?.value ?? RC_DURATION['24_MONTH']) as RC_DURATION;

            const variantId = rc.merchandiseId.split('/').pop();

            const trackingProps: ReebeloCareTrackRequest = {
              entry_point: 'Cart Pop-up',
              reebelocare_id: Number(variantId ?? 0),
              device_psku: psku ?? '',
              device_vendor: rcFor.merchandise.product.vendor ?? '',
              device_sku: rcFor.merchandise.sku ?? '',
              duration,
            };

            trackReebeloCareAdded(trackingProps);
          }
        }

        return result;
      },
      { concurrency: 1 },
    )
      .then(() => {
        onContinue();
      })
      .catch((error) => {
        setIsCheckingOut(false);
        logger.error({ error }, 'Error adding upsell reebelocare');
      });
  };

  return (
    <section className="text-sm">
      {warrantyBenefits ? (
        <CustomHeader warrantyBenefits={warrantyBenefits} />
      ) : (
        <DefaultHeader />
      )}
      {isAdditionalMonthsEnabled ? (
        <div className="divide-y divide-[#D2D3D3] px-4 pt-4 md:px-8 md:pt-8">
          <ul className="space-y-4 divide-y divide-[#D2D3D3] md:space-y-8">
            {warrantyUpsellList.map((item) => (
              <MultiLineItem
                device={item.device}
                key={item.device.id}
                productWarranty={item.productWarranty}
                rcToBeAdded={rcToBeAdded}
                setRcToBeAdded={setRcToBeAdded}
                isCheckingOut={isCheckingOut}
              />
            ))}
          </ul>
          <div className="sticky bottom-0 mt-5 bg-white py-5">
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <button
                onClick={() => {
                  setIsCheckingOut(true);
                  onContinue();
                }}
                type="button"
                className="flex h-10 w-full items-center justify-center rounded-lg border border-gray-700 bg-white text-xs font-bold text-gray-700 underline-offset-2 outline-none hover:underline"
              >
                {generateWarrantyUpsellCta().reject}
              </button>
              <button
                disabled={isCheckingOut}
                type="button"
                className="flex h-10 w-full items-center justify-center rounded-lg border border-gray-700 bg-gray-700 text-xs font-bold text-white underline-offset-2 outline-none hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => onRcContinue()}
              >
                {isCheckingOut ? (
                  <Loading size="xs" />
                ) : (
                  generateWarrantyUpsellCta().accept
                )}
              </button>
            </div>
            <WarrantyDisclaimer className="pt-4" />
          </div>
        </div>
      ) : (
        <section className="px-5 pt-5 sm:px-8 sm:pt-8">
          <div className="divide-y rounded-lg border p-5">
            {warrantyUpsellList.map((item) => (
              <LineItem
                key={item.device.id}
                device={item.device}
                productWarranty={item.productWarranty}
                rcToBeAdded={rcToBeAdded}
                setRcToBeAdded={setRcToBeAdded}
                isCheckingOut={isCheckingOut}
              />
            ))}
          </div>
          <div className="sticky bottom-0 bg-white py-5">
            <div className="flex flex-col gap-2  sm:flex-row">
              <button
                onClick={() => {
                  setIsCheckingOut(true);
                  onContinue();
                }}
                type="button"
                className=" flex h-10 w-full items-center justify-center rounded-lg border border-gray-700 bg-white text-xs font-bold text-gray-700 underline-offset-2 outline-none hover:underline"
              >
                {generateWarrantyUpsellCta().reject}
              </button>
              <button
                onClick={() => onRcContinue()}
                type="button"
                className="flex h-10 w-full items-center justify-center rounded-lg border border-gray-700 bg-gray-700 text-xs font-bold text-white underline-offset-2 outline-none hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isCheckingOut ? (
                  <Loading size="xs" />
                ) : (
                  generateWarrantyUpsellCta().accept
                )}
              </button>
            </div>
            <WarrantyDisclaimer className="mt-2.5" />
          </div>
        </section>
      )}
    </section>
  );
};

export default MultipleDevices;
